// src/components/InformationsSociete.jsx
import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from "react";
import interact from "interactjs";
import axiosInstance from "../axiosInstance";
import { SocieteContext } from "../context/SocieteContext";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

/* ============================== Constantes UI ============================== */
const PREVIEW_SCALE = 0.93;           // zoom de la page A4
const PREVIEW_MAX_HEIGHT = 340;       // hauteur visible de l'aperçu
const LIB_THUMB_SIZE = 84;            // vignettes logos
const MAX_LOGOS_ON_PAGE = 2;          // logos max positionnés

const LEGAL_FORMS = [
  "Auto-entrepreneur", "Entreprise individuelle (EI)", "EURL", "SARL", "SASU", "SAS", "SA", "SNC", "SCOP", "SCEA", "SCI", "Association (loi 1901)", "Fondation",
];

/* ==================== Sécurité ressources / médias ==================== */
const API_ORIGIN = (() => {
  const b = axiosInstance?.defaults?.baseURL || "http://localhost:5000/api";
  try { return new URL(b).origin; } catch { return "http://localhost:5000"; }
})();
const getAuthToken = () => {
  const auth = axiosInstance?.defaults?.headers?.common?.Authorization;
  if (auth?.startsWith("Bearer ")) return auth.slice(7);
  try {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("jwt") || ""
    );
  } catch { return ""; }
};
const withToken = (p) => {
  if (!p) return p;
  const token = getAuthToken();
  const u = new URL(p, API_ORIGIN);
  if (token) u.searchParams.set("t", token);
  return u.toString();
};
const resolveSecuredSrc = async (pOrId) => {
  const token = getAuthToken();
  const url = typeof pOrId === "number"
    ? `${API_ORIGIN}/api/files/${pOrId}`
    : (String(pOrId).startsWith("/api/") ? `${API_ORIGIN}${pOrId}` : String(pOrId));
  if (!url.includes("/api/files/")) return url;
  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const blob = await r.blob();
  return URL.createObjectURL(blob);
};

/* ============================ Helpers divers ============================ */
const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
Quill.register(SizeStyle, true);
const SIZE_OPTIONS = ["12px", "14px", "16px", "18px", "24px", "32px"];

const safeParse = (v, fb = null) => { try { return typeof v === "string" ? JSON.parse(v) : (v ?? fb); } catch { return fb; } };
const pxNum = (n, fb) => (Number.isFinite(Number(n)) ? Number(n) : fb);

const POS_TEXT_FALLBACK = { x: 40, y: 120, width: 320, height: 130 };
const POS_LOGO_FALLBACK = { x: 420, y: 40, width: 140, height: 70 };

const hasRenderableSource = (lib) => !!(lib && (lib.fileId || (lib.src && lib.src.trim())));
const isPlacementValid = (pl, libs) => !!libs.find((l) => l.libId === pl.libId) && hasRenderableSource(libs.find((l) => l.libId === pl.libId));

// HTML par défaut pour l’en-tête (depuis le formulaire)
const buildHtmlFromForm = (i = {}) => `
  <h2 style="font-weight:700;margin:0 0 6px 0;font-size:18px">${i.nom || "Votre société"}</h2>
  ${i.forme ? `<p style="margin:2px 0">${i.forme}</p>` : ""}
  ${i.adresse ? `<p style="margin:2px 0">${i.adresse}</p>` : ""}
  <p style="margin:2px 0">${[i.ville, i.pays].filter(Boolean).join(" – ")}</p>
  ${i.telephone ? `<p style="margin:2px 0">${i.telephone}</p>` : ""}
  ${i.email ? `<p style="margin:2px 0">${i.email}</p>` : ""}
`;

/* ============================ Composant ============================ */
export default function InformationsSociete({
  onDirtyChange = () => { },
  setSaveHandler = () => { },
  infosSociete: infosProp,
  setInfosSociete: setInfosProp,
}) {
  // Contexte si dispo
  const ctx = useContext(SocieteContext) || {};
  const infosSociete = ctx.infosSociete ?? infosProp ?? {};
  const setInfosSociete = ctx.setInfosSociete ?? setInfosProp ?? (() => { });

  /* ---------- États principaux ---------- */
  const [locked, setLocked] = useState(true);
  const [blocHtml, setBlocHtml] = useState("");
  const [posTexte, setPosTexte] = useState(POS_TEXT_FALLBACK);

  // bibliothèque & placements de logos
  const [logosLib, setLogosLib] = useState([]);      // [{libId,fileId,src,file?}]
  const [primaryLibId, setPrimaryLibId] = useState(null);
  const [selectedLib, setSelectedLib] = useState(null);
  const [placements, setPlacements] = useState([]);  // [{id,libId,x,y,width,height}]

  // refs
  const previewRef = useRef(null);
  const libGridRef = useRef(null);

  // Quill
  const quillModules = useMemo(() => ({ toolbar: { container: "#toolbar-societe" } }), []);
  const quillFormats = ["size", "bold", "italic", "underline", "align", "list", "bullet", "link", "clean"];

  /* ---------- Snapshot “propreté” ---------- */
  const initialSnapRef = useRef(null);
  const makeSnap = useCallback(() => {
    const norm = (n) => Number.isFinite(Number(n)) ? Math.round(Number(n)) : 0;
    const pos = {
      x: norm(posTexte?.x), y: norm(posTexte?.y),
      width: norm(posTexte?.width), height: norm(posTexte?.height),
    };
    const placementsSnap = (placements || []).map(p => ({
      libId: p.libId, x: norm(p.x), y: norm(p.y), width: norm(p.width), height: norm(p.height),
    }));
    const logosSig = (logosLib || []).map(l => (l.fileId ? `id:${l.fileId}` : `src:${l.src || ""}`)).sort();
    const info = {
      nom: infosSociete?.nom || "",
      forme: infosSociete?.forme || "",
      adresse: infosSociete?.adresse || "",
      ville: infosSociete?.ville || "",
      pays: infosSociete?.pays || "",
      telephone: infosSociete?.telephone || "",
      email: infosSociete?.email || "",
      site: infosSociete?.site || "",
    };
    return JSON.stringify({
      blocHtml: String((blocHtml || "").trim()),
      pos, placements: placementsSnap, logosSig, info,
      primaryLibId: primaryLibId || null,
    });
  }, [blocHtml, posTexte, placements, logosLib, infosSociete, primaryLibId]);

  const dirty = useMemo(() => {
    if (!initialSnapRef.current) return false;
    return makeSnap() !== initialSnapRef.current;
  }, [makeSnap]);

  useEffect(() => { onDirtyChange(!!dirty); }, [dirty, onDirtyChange]);

  /* ---------- InteractJS: drag/resize ---------- */
  useEffect(() => {
    const container = previewRef.current;
    if (!container) return;

    try { interact(".draggable").unset(); } catch { }
    try { interact(".resizable").unset(); } catch { }
    if (locked) return;

    interact(".draggable").draggable({
      ignoreFrom: ".ui-overlay, .ui-overlay *",
      modifiers: [interact.modifiers.restrictRect({ restriction: container, endOnly: true })],
      listeners: {
        move(ev) {
          const t = ev.target;
          const type = t.getAttribute("data-type");
          const key = t.getAttribute("data-key");
          const x = (parseFloat(t.getAttribute("data-x")) || 0) + ev.dx;
          const y = (parseFloat(t.getAttribute("data-y")) || 0) + ev.dy;
          t.style.transform = `translate(${x}px, ${y}px)`;
          t.setAttribute("data-x", x); t.setAttribute("data-y", y);

          if (type === "texte") {
            setPosTexte((p) => ({ ...p, x, y, width: t.offsetWidth, height: t.offsetHeight }));
          } else if (type === "logo" && key) {
            setPlacements((prev) => prev.map((pl) =>
              pl.id === key ? { ...pl, x, y, width: t.offsetWidth, height: t.offsetHeight } : pl
            ));
          }
        },
      },
    });

    interact(".resizable").resizable({
      edges: { top: true, left: true, bottom: true, right: true },
      modifiers: [
        interact.modifiers.restrictEdges({ outer: container, endOnly: true }),
        interact.modifiers.restrictSize({
          min: { width: 40, height: 30 },
          max: { width: container.offsetWidth, height: container.offsetHeight },
        }),
      ],
      listeners: {
        move(ev) {
          const t = ev.target;
          const type = t.getAttribute("data-type");
          const key = t.getAttribute("data-key");
          let x = parseFloat(t.getAttribute("data-x")) || 0;
          let y = parseFloat(t.getAttribute("data-y")) || 0;

          t.style.width = `${ev.rect.width}px`;
          t.style.height = `${ev.rect.height}px`;

          x += ev.deltaRect.left; y += ev.deltaRect.top;
          t.style.transform = `translate(${x}px, ${y}px)`;
          t.setAttribute("data-x", x); t.setAttribute("data-y", y);

          if (type === "texte") {
            setPosTexte({ x, y, width: ev.rect.width, height: ev.rect.height });
          } else if (type === "logo" && key) {
            setPlacements((prev) => prev.map((pl) =>
              pl.id === key ? { ...pl, x, y, width: ev.rect.width, height: ev.rect.height } : pl
            ));
          }
        },
      },
    });

    return () => {
      try { interact(".draggable").unset(); } catch { }
      try { interact(".resizable").unset(); } catch { }
    };
  }, [locked]);

  /* ---------- Fermer menu vignette si clic dehors ---------- */
  useEffect(() => {
    const handler = (e) => { if (libGridRef.current && !libGridRef.current.contains(e.target)) setSelectedLib(null); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ---------- Form handlers ---------- */
  const onChangeInfo = (e) => setInfosSociete((p) => ({ ...p, [e.target.name]: e.target.value }));

  /* ---------- Nettoyage blobs au démontage ---------- */
  useEffect(() => () => {
    logosLib.forEach((l) => l.file && l.src?.startsWith("blob:") && URL.revokeObjectURL(l.src));
  }, [logosLib]);

  /* ---------- Bibliothèque logos ---------- */
  const addLogosToLibrary = (files) => {
    if (!files?.length) return;
    setLogosLib((prev) => {
      const remain = Math.max(0, MAX_LOGOS_ON_PAGE - prev.length + (prev.length ? 1 : 0));
      const next = [...files].slice(0, remain || files.length).map((f) => ({
        libId: `lib-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        fileId: null,
        src: URL.createObjectURL(f),
        file: f,
      }));
      return [...prev, ...next];
    });
  };
  const removeFromLibrary = (libId) => {
    if (!window.confirm("Supprimer ce logo de la bibliothèque ?")) return;
    setPlacements((prev) => prev.filter((p) => p.libId !== libId));
    setLogosLib((prev) => {
      const item = prev.find((l) => l.libId === libId);
      if (item?.file && item.src?.startsWith("blob:")) URL.revokeObjectURL(item.src);
      return prev.filter((l) => l.libId !== libId);
    });
    if (primaryLibId === libId) setPrimaryLibId(null);
    if (selectedLib === libId) setSelectedLib(null);
  };
  const addPlacementFromLib = (libId) => {
    const lib = logosLib.find((l) => l.libId === libId);
    if (!hasRenderableSource(lib)) return;
    setPlacements((prev) => {
      const validCount = prev.filter((p) => isPlacementValid(p, logosLib)).length;
      if (validCount >= MAX_LOGOS_ON_PAGE) {
        alert(`Vous pouvez placer au maximum ${MAX_LOGOS_ON_PAGE} logos sur la page.`);
        return prev;
      }
      return [...prev, { id: `pl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, libId, ...POS_LOGO_FALLBACK }];
    });
    setLocked(false);
    setSelectedLib(null);
    if (!primaryLibId) setPrimaryLibId(libId);
  };
  const removePlacement = (plId) => setPlacements((prev) => prev.filter((p) => p.id !== plId));
  const scalePlacement = (plId, factor) =>
    setPlacements((prev) =>
      prev.map((pl) =>
        pl.id !== plId
          ? pl
          : { ...pl, width: Math.max(40, Math.round(pl.width * factor)), height: Math.max(30, Math.round(pl.height * factor)) }
      )
    );

  /* ---------- Save ---------- */
const handleSave = useCallback(async () => {
  try {
    // 1) Upload des nouveaux fichiers (logosLib -> Cloudinary)
    const uploaded = await Promise.all(
      logosLib.map(async (it) => {
        // Pas de nouveau fichier => on garde les infos existantes
        if (!it.file) {
          return {
            libId: it.libId,
            fileId: it.fileId,
            src: it.src,
          };
        }

        const fd = new FormData();
        fd.append("file", it.file);

        const r = await axiosInstance.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const id = r.data?.id;
        const url = r.data?.url;

        return {
          libId: it.libId,
          fileId: id,
          src: withToken(url),
        };
      })
    );

    // 2) On fabrique la nouvelle bibliothèque logos (nextLib) avec fileId + src à jour
    const uploadedMap = new Map(uploaded.map((u) => [u.libId, u]));
    const nextLib = logosLib.map((l) => {
      const u = uploadedMap.get(l.libId);
      if (!u) return l;
      return {
        ...l,
        fileId: u.fileId ?? l.fileId,
        src: u.src || l.src,
        file: undefined, // on vide le buffer, plus besoin
      };
    });

    // On met à jour le state pour les prochains rendus
    setLogosLib(nextLib);

    const norm = (n, fb) => Math.round(pxNum(n, fb));

    // 3) On construit maintenant les placements à partir de nextLib (ET PAS logosLib)
    const validPlacements = placements
      .filter((p) => isPlacementValid(p, nextLib))
      .slice(0, MAX_LOGOS_ON_PAGE);

    const placementsPx = validPlacements.map((p) => {
      const lib = nextLib.find((l) => l.libId === p.libId);
      const fileId = lib?.fileId ?? null;
      const src = lib?.src
        ? withToken(lib.src)
        : fileId
        ? withToken(`/api/files/${fileId}`)
        : "";

      return {
        id: p.id,
        libId: p.libId,
        file_id: fileId,
        src,
        x: norm(p.x, POS_LOGO_FALLBACK.x),
        y: norm(p.y, POS_LOGO_FALLBACK.y),
        width: norm(p.width, POS_LOGO_FALLBACK.width),
        height: norm(p.height, POS_LOGO_FALLBACK.height),
      };
    });

    // 4) Logo principal (toujours basé sur nextLib)
    const primary = primaryLibId
      ? nextLib.find((l) => l.libId === primaryLibId)
      : null;

    const logo_file_id = primary?.fileId ?? (placementsPx[0]?.file_id ?? null);

    // 5) Payload complet pour /entreprise
    const payload = {
      nom: infosSociete?.nom || "",
      forme: infosSociete?.forme || "",
      adresse: infosSociete?.adresse || "",
      ville: infosSociete?.ville || "",
      pays: infosSociete?.pays || "",
      telephone: infosSociete?.telephone || "",
      email: infosSociete?.email || "",
      site: infosSociete?.site || "",
      bloc_html: (blocHtml || "").trim(),
      positiontexte: {
        x: norm(posTexte.x, POS_TEXT_FALLBACK.x),
        y: norm(posTexte.y, POS_TEXT_FALLBACK.y),
        width: norm(posTexte.width, POS_TEXT_FALLBACK.width),
        height: norm(posTexte.height, POS_TEXT_FALLBACK.height),
      },
      logos_on_page: placementsPx,
      logo_file_id,
    };

    // 6) Sauvegarde en base
    await axiosInstance.put("/entreprise", payload);

    // Mise à jour du context
    setInfosSociete((prev) => ({ ...prev, ...(payload || {}) }));
    setLocked(true);

    // Baseline "propre"
    initialSnapRef.current = makeSnap();
    onDirtyChange(false);

    alert("✅ Infos société enregistrées.");
  } catch (e) {
    console.error("❌ Enregistrement infos société :", e);
    alert(
      e?.response?.data?.message || e.message || "Une erreur est survenue."
    );
  }
}, [
  logosLib,
  placements,
  primaryLibId,
  infosSociete,
  blocHtml,
  posTexte,
  setInfosSociete,
  onDirtyChange,
  makeSnap,
]);


  // Fournir le handler au parent (pop-up “Enregistrer et quitter”)
  useEffect(() => {
    setSaveHandler(() => handleSave);
    return () => setSaveHandler(null);
  }, [setSaveHandler, handleSave]);

  /* ---------- Chargement initial ---------- */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/entreprise");
        const ent = data || {};

        const onPageArr = safeParse(ent.logos_on_page, []);
        const lib = [];

        // logos depuis les placements
        for (let i = 0; i < onPageArr.length; i++) {
          const p = onPageArr[i] || {};
          const src = p.src ? withToken(p.src) : (p.file_id ? withToken(`/api/files/${p.file_id}`) : "");
          if (!src) continue;
          lib.push({ libId: p.libId || `lib-${i}`, fileId: p.file_id ?? null, src });
        }
        // ajouter logo principal si absent
        if (ent.logo_file_id && !lib.some(l => l.fileId === ent.logo_file_id)) {
          lib.unshift({ libId: "lib-main", fileId: ent.logo_file_id, src: withToken(`/api/files/${ent.logo_file_id}`) });
        }
        // déduplication
        const seen = new Set(); const unique = [];
        for (const l of lib) {
          const k = l.fileId ? `id:${l.fileId}` : `src:${l.src}`;
          if (seen.has(k)) continue; seen.add(k); unique.push(l);
        }
        setLogosLib(unique);

        // placements reliés à la lib unique
        const placementsInit = onPageArr.map((p, i) => {
          const match = unique.find(l =>
            (p.file_id && l.fileId === p.file_id) ||
            (p.src && withToken(p.src) === l.src)
          );
          const libId = match?.libId || p.libId || `lib-${i}`;
          return {
            id: p.id || `pl-${i}`,
            libId,
            x: pxNum(p.x, POS_LOGO_FALLBACK.x),
            y: pxNum(p.y, POS_LOGO_FALLBACK.y),
            width: pxNum(p.width, POS_LOGO_FALLBACK.width),
            height: pxNum(p.height, POS_LOGO_FALLBACK.height) || POS_LOGO_FALLBACK.height,
          };
        });
        setPlacements(placementsInit);

        // position texte
        const t = safeParse(ent.positiontexte, null);
        setPosTexte(t ? {
          x: pxNum(t.x, POS_TEXT_FALLBACK.x),
          y: pxNum(t.y, POS_TEXT_FALLBACK.y),
          width: pxNum(t.width, POS_TEXT_FALLBACK.width),
          height: pxNum(t.height, POS_TEXT_FALLBACK.height),
        } : POS_TEXT_FALLBACK);

        // HTML bloc
        setBlocHtml((ent.bloc_html || "").trim() || buildHtmlFromForm(ent));
        setInfosSociete(p => ({ ...p, ...(ent || {}) }));

        // principal
        if (ent.logo_file_id) {
          const match = unique.find(l => l.fileId === ent.logo_file_id);
          setPrimaryLibId(match?.libId || null);
        } else if (unique[0]) setPrimaryLibId(unique[0].libId);

        setLocked(true);

        // >>> baseline après hydratation
        initialSnapRef.current = JSON.stringify({
          blocHtml: String(((ent.bloc_html || "").trim() || buildHtmlFromForm(ent))),
          pos: {
            x: Math.round(pxNum((t?.x), POS_TEXT_FALLBACK.x)),
            y: Math.round(pxNum((t?.y), POS_TEXT_FALLBACK.y)),
            width: Math.round(pxNum((t?.width), POS_TEXT_FALLBACK.width)),
            height: Math.round(pxNum((t?.height), POS_TEXT_FALLBACK.height)),
          },
          placements: placementsInit.map(p => ({
            libId: p.libId, x: Math.round(p.x), y: Math.round(p.y), width: Math.round(p.width), height: Math.round(p.height),
          })),
          logosSig: unique.map(l => (l.fileId ? `id:${l.fileId}` : `src:${l.src || ""}`)).sort(),
          info: {
            nom: ent?.nom || "", forme: ent?.forme || "", adresse: ent?.adresse || "",
            ville: ent?.ville || "", pays: ent?.pays || "", telephone: ent?.telephone || "",
            email: ent?.email || "", site: ent?.site || "",
          },
          primaryLibId: (ent.logo_file_id ? (unique.find(l => l.fileId === ent.logo_file_id)?.libId ?? null) : (unique[0]?.libId ?? null)) || null,
        });
        onDirtyChange(false);
      } catch (e) {
        console.error("get /entreprise", e);
        setLocked(false);
      }
    })();
  }, [setInfosSociete, onDirtyChange]);

  /* ============================ Rendu ============================ */
  return (
    <div className="w-full">
      <style>{`
        .quill14 .ql-editor{font-size:14px}
        .ql-container{max-height:none;overflow:visible}
        input[type=number]{-moz-appearance:textfield;appearance:textfield}
        input[type=number]::-webkit-outer-spin-button,input[type=number]::-webkit-inner-spin-button{ -webkit-appearance:none;margin:0 }
        .card{border-radius:12px;border:1px solid #e5e7eb;background:#fff}
        .card-hd{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid #eef1f4}
        .card-tt{font-weight:700;color:#0f172a}
        .card-bd{padding:16px}
        .a4-wrap{ --scale:${PREVIEW_SCALE}; width: calc(794px * var(--scale)); height: ${PREVIEW_MAX_HEIGHT}px;
                  overflow:hidden; border:1px solid #e5e7eb; border-radius:12px; background:#fff; box-shadow:0 2px 8px rgba(0,0,0,.03) }
        .a4-inner{ width:794px; height:1123px; transform: scale(var(--scale)); transform-origin: top left; position:relative; }
      `}</style>

      <div className="mx-auto w-full max-w-[1400px] px-3 sm:px-4 lg:px-6 space-y-8">
        {/* 1) FORMULAIRE EN PREMIER */}
        <section className="card w-full">
          <div className="card-hd">
            <div className="card-tt">Informations société</div>
          </div>
          <div className="card-bd space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">Nom</label>
                <input type="text" name="nom" value={infosSociete?.nom || ""} onChange={onChangeInfo}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">Forme juridique</label>
                <select name="forme" value={infosSociete?.forme || ""} onChange={onChangeInfo}
                  className="w-full border rounded px-3 py-2 bg-white focus:ring-2 focus:ring-sky-500 focus:border-sky-500">
                  <option value="">— Sélectionner —</option>
                  {LEGAL_FORMS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">Adresse</label>
                <input type="text" name="adresse" value={infosSociete?.adresse || ""} onChange={onChangeInfo}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">Ville</label>
                <input type="text" name="ville" value={infosSociete?.ville || ""} onChange={onChangeInfo}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">Pays</label>
                <input type="text" name="pays" value={infosSociete?.pays || ""} onChange={onChangeInfo}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">Téléphone</label>
                <input type="text" name="telephone" value={infosSociete?.telephone || ""} onChange={onChangeInfo}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">Email</label>
                <input type="email" name="email" value={infosSociete?.email || ""} onChange={onChangeInfo}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-800 mb-1">Site</label>
                <input type="text" name="site" value={infosSociete?.site || ""} onChange={onChangeInfo}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500" />
              </div>
            </div>
          </div>
        </section>

        {/* 2) EN-TÊTE + 3) BIBLIOTHÈQUE LOGOS côte à côte */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* 2) ÉDITEUR D’EN-TÊTE (à gauche) */}
          <section className="card">
            <div className="card-hd">
              <div className="card-tt">En-tête affiché sur devis & factures</div>
              {/* plus d’actions ici */}
            </div>

            <div className="card-bd">
              <div id="toolbar-societe" className="ql-toolbar ql-snow rounded-t-md">
                <select className="ql-size" defaultValue="">
                  <option value="">Défaut</option>
                  {SIZE_OPTIONS.map(v => <option key={v} value={v}>{parseInt(v, 10)}</option>)}
                </select>
                <button className="ql-bold" />
                <button className="ql-italic" />
                <button className="ql-underline" />
                <select className="ql-align" />
                <button className="ql-list" value="ordered" />
                <button className="ql-list" value="bullet" />
                <button className="ql-link" />
                <button className="ql-clean" />
              </div>

              <ReactQuill
                theme="snow"
                value={blocHtml}
                onChange={setBlocHtml}
                placeholder="Saisissez l’en-tête (tailles, gras, listes, alignements…)."
                modules={quillModules}
                formats={quillFormats}
                className="quill14 rounded-b-md bg-white"
              />
            </div>
          </section>

          {/* 3) BIBLIOTHÈQUE LOGOS (à droite) */}
          <section className="card">
            <div className="card-hd">
              <div className="card-tt">Bibliothèque logos</div>
              {/* plus d’actions ici */}
            </div>

            <div className="card-bd">
              <div ref={libGridRef} className="grid grid-cols-4 md:grid-cols-6 gap-3">
                {logosLib.map((l) => {
                  const isSel = selectedLib === l.libId;
                  const isPrim = primaryLibId === l.libId;
                  return (
                    <div key={l.libId} className="text-center">
                      <button
                        type="button"
                        className="block w-full"
                        onClick={() => setSelectedLib(s => s === l.libId ? null : l.libId)}
                        aria-label="Sélectionner ce logo"
                      >
                        <div
                          className={`mx-auto rounded-lg overflow-hidden bg-white border shadow-sm ${isSel ? "ring-2 ring-sky-500" : ""}`}
                          style={{ width: LIB_THUMB_SIZE, height: LIB_THUMB_SIZE }}
                        >
                          {l.src ? (
                            <img
                              src={l.src}
                              alt="logo"
                              className="w-full h-full object-contain"
                              onError={async () => {
                                try {
                                  const fixed = await resolveSecuredSrc(l.src || (l.fileId ? `/api/files/${l.fileId}` : ""));
                                  setLogosLib(prev => prev.map(x => x.libId === l.libId ? { ...x, src: fixed } : x));
                                } catch { }
                              }}
                            />
                          ) : (
                            <div className="w-full h-full grid place-items-center text-xs text-slate-400">vide</div>
                          )}
                        </div>
                      </button>

                      <div className="mt-1 text-[11px] text-slate-600">
                        <label className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            name="primary-logo"
                            checked={isPrim}
                            onChange={() => setPrimaryLibId(l.libId)}
                          />
                          principal
                        </label>
                      </div>

                      {isSel && (
                        <div className="flex justify-center gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => addPlacementFromLib(l.libId)}
                            className="px-3 py-1.5 text-xs rounded-md border border-slate-300 hover:bg-slate-50"
                          >
                            Ajouter
                          </button>
                          <button
                            type="button"
                            onClick={() => removeFromLibrary(l.libId)}
                            className="px-3 py-1.5 text-xs rounded-md border border-slate-300 hover:bg-slate-50"
                          >
                            Supprimer
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {!logosLib.length && (
                  <div className="text-sm text-slate-500 col-span-full">
                    Aucune image. Utilisez <strong>« Ajouter un logo »</strong>.
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* BARRE D’ACTIONS COMMUNE (sous les deux blocs) */}
        <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
          {/* 1) Enregistrer */}
          <button
            type="button"
            onClick={handleSave}
            className="h-9 px-4 rounded-full text-[13px] bg-sky-700 text-white hover:bg-sky-600"
          >
            Enregistrer
          </button>

          {/* 2) Ajouter un logo */}
          <label className="inline-flex items-center gap-2 h-9 px-3 rounded-full text-[13px] border bg-sky-700 text-white cursor-pointer hover:bg-sky-600">
            Ajouter un logo
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                addLogosToLibrary(e.target.files);
                e.target.value = "";
              }}
            />
          </label>

          {/* 3) Modifier / Verrouiller la mise en page (toggle) */}
          {locked ? (
            <button
              type="button"
              onClick={() => setLocked(false)}
              className="h-9 px-3 rounded-full text-[13px] bg-slate-700 text-white hover:bg-slate-600"
            >
              Modifier la mise en page
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setLocked(true)}
              className="h-9 px-3 rounded-full text-[13px] bg-emerald-700 text-white hover:bg-emerald-600"
            >
              Verrouiller la mise en page
            </button>
          )}
        </div>

        {/* 4) APERÇU A4 */}
        <div className="w-full flex justify-center">
          <div className="a4-wrap">
            <div className="a4-inner" ref={previewRef}>
              {/* Carte client factice */}
              <div
                className="absolute z-40 top-[140px] right-[40px] min-w-[300px] max-w-[500px] bg-white border border-gray-300 shadow-md rounded p-4 text-sm"
                style={{ pointerEvents: "none" }}
              >
                <h2 className="text-md font-semibold mb-2">Nom du client</h2>
                <div className="text-gray-700 space-y-1">
                  <p>Adresse du client</p><p>Code postal Ville</p>
                </div>
              </div>

              {/* Logos placés */}
              {placements.map((pl) => {
                const lib = logosLib.find((l) => l.libId === pl.libId);
                if (!lib) return null;
                return (
                  <div
                    key={pl.id}
                    className={!locked ? "draggable resizable" : ""}
                    data-type="logo"
                    data-key={pl.id}
                    data-x={pl.x}
                    data-y={pl.y}
                    style={{
                      transform: `translate(${pl.x}px, ${pl.y}px)`,
                      width: pl.width, height: pl.height,
                      position: "absolute", zIndex: 60,
                      border: locked ? "none" : "1px dashed #94a3b8",
                      background: "transparent",
                    }}
                  >
                    {!locked && (
                      <div className="ui-overlay absolute top-2 right-2 z-[200] flex gap-1 pointer-events-auto">
                        <button className="h-5 w-5 rounded-full border border-slate-400 bg-white text-[10px] leading-5 text-slate-700 shadow-sm"
                          title="Agrandir" onClick={(e) => { e.stopPropagation(); scalePlacement(pl.id, 1.12); }}>＋</button>
                        <button className="h-5 w-5 rounded-full border border-slate-400 bg-white text-[10px] leading-5 text-slate-700 shadow-sm"
                          title="Réduire" onClick={(e) => { e.stopPropagation(); scalePlacement(pl.id, 1 / 1.12); }}>－</button>
                        <button className="h-5 w-5 rounded-full border border-slate-400 bg-white text-[10px] leading-5 text-rose-600 shadow-sm"
                          title="Retirer" onClick={(e) => { e.stopPropagation(); removePlacement(pl.id); }}>×</button>
                      </div>
                    )}
                    {lib.src && (
                      <img
                        src={lib.src}
                        alt="logo société"
                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        draggable={false}
                        onError={async () => {
                          try {
                            const fixed = await resolveSecuredSrc(lib.src || (lib.fileId ? `/api/files/${lib.fileId}` : ""));
                            setLogosLib(prev => prev.map(x => x.libId === lib.libId ? { ...x, src: fixed } : x));
                          } catch { }
                        }}
                      />
                    )}
                  </div>
                );
              })}

              {/* Bloc texte */}
              <div
                className={!locked ? "draggable resizable" : ""}
                data-type="texte"
                data-key="texte"
                data-x={posTexte.x}
                data-y={posTexte.y}
                style={{
                  transform: `translate(${posTexte.x}px, ${posTexte.y}px)`,
                  width: posTexte.width || POS_TEXT_FALLBACK.width,
                  height: posTexte.height || "auto",
                  position: "absolute", zIndex: 45,
                  border: locked ? "none" : "1px dashed #94a3b8",
                  padding: locked ? "0px" : "6px",
                  background: locked ? "transparent" : "rgba(255,255,255,0.65)",
                  overflow: "visible",
                }}
              >
                <div
                  className="ql-editor !p-0 !leading-snug"
                  style={{ overflow: "visible", maxHeight: "none" }}
                  dangerouslySetInnerHTML={{ __html: (blocHtml && blocHtml.trim()) || buildHtmlFromForm(infosSociete) }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
